/**
 * TruePrice V1 - Verdict Types
 * 
 * Defines the complete verdict schema including all calculation outputs,
 * validation flags, and forensic markers.
 * Source: SSOT, Addendums C, D, E
 * 
 * UPDATED: Added AIInsight for persisted roast content
 */

import { MediumKey } from './medium';
import { TierKey } from './tier';
import { CareerStageKey } from './career';
import type { AnalysisResponse } from './narration';

/**
 * AI-generated pricing insight from PricingCoach
 * Stored with verdict to avoid regenerating (saves tokens, preserves original roast)
 */
/**
 * Unified forensic roast data (V2). One analysis per verdict, cached at save time.
 */
export interface ForensicRoastData {
  analysis: string;
  status: 'Below Floor' | 'Supported' | 'Above Market Band';
  isEstimated: boolean;
  generatedAt: string;       // ISO timestamp
  inputHash: string;         // hash of inputs — detect changes
}

/** @deprecated Use ForensicRoastData. Legacy PricingCoach output. */
export interface AIInsight {
  insight: string;      // Main roast observation (60% of content)
  toughLove: string;    // Harsh truth (20%)
  sage: string;         // Wisdom/perspective (20%)
  nextStep: string;     // Actionable recommendation
  model: string;        // Which LLM model generated this
  generatedAt: string;  // ISO timestamp when generated
}

/**
 * Raw inputs provided by the user for a verdict calculation
 */
export interface VerdictInputs {
  // Artwork details
  medium: MediumKey;
  width: number;                // Inches
  height: number;               // Inches
  hours: number;                // Total hours worked
  hoursTracked?: boolean;       // True if hours were tracked, false if estimated
  materials: number;            // Cents. Total material costs (materialEstimate + framingEstimate).
  materialEstimate?: number;    // Cents. P7 material estimate (or manual override).
  framingEstimate?: number;     // Cents. Framing cost for paper mediums (0 for canvas mediums).
  framingWaived?: boolean;      // True if artist checked "I frame my own work".
  gutPrice: number | null;      // Cents. User's instinctual price. Null = not provided.
  title?: string;               // Optional artwork title
  status?: 'available' | 'commission' | 'sold'; // Artwork lifecycle status
  
  // Fidelity
  tier: TierKey;
  
  // Profile context (pulled from user profile)
  monthlyBurn: number;          // Cents
  monthlyCapacity: number;      // Hours
  careerStage: CareerStageKey;
  highestSale: number | null;   // Cents. For calibration gate.

  // Substrate (ceiling modifier — market perception, not cost)
  substrate?: string;           // Key from SUBSTRATE_MODIFIERS (e.g., "stretched_canvas", "cotton_rag")
  substrateModifier?: number;   // The multiplier applied to ceiling (for audit trail)

  // Sales channel (output-layer only — does not affect engine calculations)
  salesChannel?: 'direct' | 'gallery' | 'online_platform' | 'consignment';

  // Metadata
  currency: string;             // ISO 4217 (e.g., "USD")
}

/**
 * Calculated intermediate values
 */
export interface VerdictCalculations {
  // Area
  area: number;                 // Square inches (width × height)
  
  // Floor calculation
  survivalTarget: number;       // ST = burn / capacity (cents per hour)
  laborValue: number;           // Cents. H × ST × F
  baseCost: number;             // Cents. laborValue + materials
  scopeTax: number;             // Cents. baseCost × T
  ttcp: number;                 // Cents. Total True Cost Price (floor)
  
  // Ceiling calculation
  psiBase: number;              // $/sq in before adjustments
  psiEffective: number;         // $/sq in after detail premium
  sizeMultiplier: number;       // S_size from decay curve
  detailPremium: number;        // 1.0–1.5 multiplier for L4+ small works
  ceiling: number;              // Cents. P_max (market ceiling)
  
  // Comparison metrics
  delta: number | null;         // Cents. GP - TTCP (null if no gut price)
  effectiveHourly: number | null; // Cents. GP / H — gross rate (null if no gut price)
  sustainabilityRatio: number | null; // EH / ST (null if no gut price)
  
  // Density metrics
  density: number;              // Hours per square inch
  densityMin: number;           // Tier minimum (after relaxer)
  densityMax: number;           // Tier maximum (after relaxer)
}

/**
 * Validation and exception flags
 * Source: Addendum E (all P-GLOBAL patches)
 */
export interface VerdictFlags {
  // Vetoes and warnings
  isSpeedVeto: boolean;              // Density below tier minimum
  suggestedTier: TierKey | null;     // If speed veto, suggest this tier
  isOverDensity: boolean;            // Density above tier maximum (informational)
  isCeilingBelowFloor: boolean;      // P_max < TTCP (market warning)
  
  // Exception conditions applied
  masteryOverrideApplied: boolean;   // P-GLOBAL-02: Established+ bypasses veto
  materialExceptionApplied: boolean; // P-GLOBAL-07: Materials > 40% of gutPrice (or TTCP fallback)
  materialCheckBase: 'actual' | 'estimated'; // 'actual' = gutPrice used, 'estimated' = TTCP fallback
  smallWorkLogicApplied: boolean;    // P-GLOBAL-05: Area < 144 sq in
  minimumFloorApplied: boolean;      // P-GLOBAL-04: Area < 25 sq in
  detailPremiumApplied: boolean;     // P-GLOBAL-08: L4+ small work boost
  masterBoostApplied: boolean;       // Established PSI override
  densityRelaxerApplied: boolean;    // P-WATERCOLOR-01: Paper medium -30%
  calibrationOverride: boolean;      // P-GLOBAL-06: Career stage was downgraded
  
  // Sustainability status
  sustainabilityStatus: SustainabilityStatus;
}

export type SustainabilityStatus = 'death_zone' | 'maintenance' | 'growth' | 'unknown';

/**
 * Workflow stage for pipeline management
 * Source: Design System Migration (Phase 4)
 */
export type WorkflowStage = 'audit' | 'quote' | 'verified' | 'sold';

/**
 * Trust score for data quality assessment
 * Source: Design System Migration (Phase 4)
 */
export interface TrustScore {
  value: number;              // 0-100 score
  type: 'tracked' | 'estimated'; // Based on hoursTracked
  lastUpdated: string;        // ISO timestamp
}

/**
 * Complete verdict record stored in Firestore
 */
export interface Verdict {
  // Identity
  id: string;                   // Auto-generated ID
  userId: string;               // Owner's UID

  // Inputs (preserved for audit trail)
  inputs: VerdictInputs;

  // Calculations
  calculations: VerdictCalculations;

  // Flags
  flags: VerdictFlags;

  // Unified forensic roast (V2) — one analysis, cached at save time
  forensicRoast?: ForensicRoastData;

  // Reverse trial flag — set to true when this verdict was the user's first
  // free Price Check (and therefore included a Forensic Roast for free).
  // Used to keep showing the roast on this verdict even after Check #2-3.
  hadFreeRoast?: boolean;

  /** @deprecated Use forensicRoast. Legacy PricingCoach output. */
  aiInsight?: AIInsight;

  /** @deprecated Use forensicRoast. Legacy narration/roast output. */
  aiAnalysis?: AnalysisResponse;

  // Sale tracking
  sold: boolean;
  soldPrice: number | null;     // Cents
  soldDate: string | null;      // ISO date string
  soldNote?: string;            // Optional note about the sale
  soldBuyer?: string;           // Buyer name (optional)

  // Workflow management (Design System Migration)
  // Added in V2.0 - optional for backward compatibility
  workflowStage?: WorkflowStage;  // Pipeline stage (default: 'audit')
  trustScore?: TrustScore;         // Data quality score
  migrationVersion?: number;       // Schema version (1 = v2.0 fields added)

  // Reconciliation (Phase 4)
  linkedTransactionId?: string | null;       // transaction.id if linked to a bank transaction
  reconciliationStatus?:
    | 'none'               // not sold
    | 'reported'           // sold, no bank txn yet (Pool 2)
    | 'confirmed'          // sold + linked to bank txn (Pool 1)
    | 'confirmed_manual'   // sold, artist says no bank record expected
    | null;                // legacy verdicts (treat as 'reported' if sold === true)
  reconciliationNudgedAt?: string | null;    // ISO date of last 30-day nudge shown

  // Soft delete
  deletedAt?: string | null;    // ISO date string, null = not deleted

  // Sales channel retail prices (output-layer — does not affect engine calculations)
  salesChannel?: 'direct' | 'gallery' | 'online_platform' | 'consignment';
  channelCommission?: number;   // Commission rate applied (0.0–1.0)
  retailFloor?: number;         // Cents. floor / (1 - commission). Absent for direct.
  retailCeiling?: number;       // Cents. ceiling / (1 - commission). Absent for direct.

  // Metadata
  currency: string;             // ISO 4217 (copied from inputs for easy access)
  createdAt: string;            // ISO date string
  updatedAt: string;            // ISO date string
}

/**
 * Summary for list views (Portfolio tab)
 */
export interface VerdictSummary {
  id: string;
  medium: MediumKey;
  width: number;
  height: number;
  title?: string;                   // Artwork title (primary identifier)
  ttcp: number;
  gutPrice: number | null;
  ceiling: number;
  delta: number | null;
  effectiveHourly: number | null;
  sustainabilityStatus: SustainabilityStatus;
  sold: boolean;
  soldPrice: number | null;
  status?: 'available' | 'commission' | 'sold';
  hoursTracked?: boolean;
  savedMonthlyBurn?: number;        // Cents, burn at calculation time
  workflowStage?: WorkflowStage;    // Pipeline stage
  trustScore?: TrustScore;          // Trust score
  hadFreeRoast?: boolean;           // Reverse-trial flag
  createdAt: string;
}

/**
 * Extract summary from full verdict
 */
export function toVerdictSummary(verdict: Verdict): VerdictSummary {
  return {
    id: verdict.id,
    medium: verdict.inputs.medium,
    width: verdict.inputs.width,
    height: verdict.inputs.height,
    title: verdict.inputs.title,
    ttcp: verdict.calculations.ttcp,
    gutPrice: verdict.inputs?.gutPrice ?? null,
    ceiling: verdict.calculations.ceiling,
    delta: verdict.calculations.delta,
    effectiveHourly: verdict.calculations.effectiveHourly,
    sustainabilityStatus: verdict.flags.sustainabilityStatus,
    sold: verdict.sold,
    soldPrice: verdict.soldPrice,
    status: verdict.inputs.status,
    hoursTracked: verdict.inputs.hoursTracked,
    savedMonthlyBurn: verdict.inputs.monthlyBurn,
    workflowStage: verdict.workflowStage,
    trustScore: verdict.trustScore,
    hadFreeRoast: verdict.hadFreeRoast,
    createdAt: verdict.createdAt,
  };
}

/**
 * Determine sustainability status from ratio
 * Source: SSOT sustainability metrics
 */
export function getSustainabilityStatus(ratio: number | null): SustainabilityStatus {
  if (ratio === null) return 'unknown';
  if (ratio >= 1.2) return 'growth';
  if (ratio >= 1.0) return 'maintenance';
  return 'death_zone';
}
